import React, { useState } from "react";
import {
  Calendar,
  Button,
  Popover,
  Typography,
  Space,
  Badge,
  theme,
} from "antd";
import {
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import localeData from "dayjs/plugin/localeData";

import type { Dayjs } from "dayjs";

// Extend dayjs with isoWeek plugin
dayjs.extend(isoWeek);
dayjs.extend(localeData);

const { Text } = Typography;

interface WeekCalendarSelectorProps {
  currentWeek: Date;
  onWeekChange: (week: Date) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onCurrentWeek: () => void;
}

const WeekCalendarSelector: React.FC<WeekCalendarSelectorProps> = ({
  currentWeek,
  onWeekChange,
  onPrevWeek,
  onNextWeek,
  onCurrentWeek,
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarValue, setCalendarValue] = useState(dayjs(currentWeek));

  // Get theme token for dark mode compatibility
  const { token } = theme.useToken();

  const handleDateSelect = (date: Dayjs) => {
    const selectedWeek = date.startOf("week").toDate();
    onWeekChange(selectedWeek);
    setCalendarValue(date);
    setCalendarOpen(false);
  };

  const handleCalendarChange = (date: Dayjs) => {
    setCalendarValue(date);
  };

  // Custom cell render to highlight weeks
  const cellRender = (current: Dayjs) => {
    const currentDate = current.toDate();
    const isCurrentWeek = dayjs(currentDate).isSame(dayjs(currentWeek), "week");
    const isCurrentDay = dayjs(currentDate).isSame(dayjs(), "day");

    return (
      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isCurrentWeek && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: token.colorPrimaryBg,
              borderRadius: 4,
              border: `2px solid ${token.colorPrimary}`,
              zIndex: 0,
            }}
          />
        )}
        {isCurrentDay && (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 6,
              height: 6,
              backgroundColor: token.colorError,
              borderRadius: "50%",
              zIndex: 2,
            }}
          />
        )}
      </div>
    );
  };

  const calendarContent = (
    <div style={{ width: 320 }}>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: `1px solid ${token.colorBorder}`,
          backgroundColor: token.colorFillAlter,
        }}
      >
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Text strong style={{ color: token.colorText }}>
            Select Week
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Click on any day to select that week
          </Text>
        </Space>
      </div>

      <Calendar
        fullscreen={false}
        value={calendarValue}
        onSelect={handleDateSelect}
        onChange={handleCalendarChange}
        cellRender={cellRender}
        headerRender={({ value, type, onChange, onTypeChange }) => {
          const start = 0;
          const end = 12;
          const monthOptions = [];

          let current = value.clone();
          const localeData = value.localeData();
          const months = [];
          for (let i = 0; i < 12; i++) {
            current = current.month(i);
            months.push(localeData.monthsShort(current));
          }

          for (let i = start; i < end; i++) {
            monthOptions.push(
              <option key={i} value={i}>
                {months[i]}
              </option>,
            );
          }

          const year = value.year();
          const month = value.month();
          const options = [];
          for (let i = year - 10; i < year + 10; i += 1) {
            options.push(
              <option key={i} value={i}>
                {i}
              </option>,
            );
          }

          return (
            <div
              style={{
                padding: "8px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `1px solid ${token.colorBorder}`,
                backgroundColor: token.colorBgContainer,
              }}
            >
              <Space>
                <select
                  value={month}
                  onChange={(e) => {
                    const newValue = value
                      .clone()
                      .month(parseInt(e.target.value, 10));
                    onChange(newValue);
                  }}
                  style={{
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 14,
                    backgroundColor: token.colorBgContainer,
                    color: token.colorText,
                  }}
                >
                  {monthOptions}
                </select>
                <select
                  value={year}
                  onChange={(e) => {
                    const newValue = value
                      .clone()
                      .year(parseInt(e.target.value, 10));
                    onChange(newValue);
                  }}
                  style={{
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 14,
                    backgroundColor: token.colorBgContainer,
                    color: token.colorText,
                  }}
                >
                  {options}
                </select>
              </Space>

              <Button
                size="small"
                type="link"
                onClick={() => {
                  const today = dayjs();
                  onChange(today);
                  setCalendarValue(today);
                }}
                style={{ padding: 0, height: "auto" }}
              >
                Today
              </Button>
            </div>
          );
        }}
      />

      <div
        style={{
          padding: "12px 16px",
          borderTop: `1px solid ${token.colorBorder}`,
          backgroundColor: token.colorFillAlter,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Current: {dayjs(currentWeek).format("MMM D, YYYY")}
        </Text>
        <Button
          size="small"
          type="primary"
          onClick={() => setCalendarOpen(false)}
        >
          Close
        </Button>
      </div>
    </div>
  );

  const weekStart = dayjs(currentWeek).format("MMM D");
  const weekEnd = dayjs(currentWeek).add(6, "day").format("MMM D, YYYY");

  return (
    <Space.Compact size="middle">
      <Button
        icon={<LeftOutlined />}
        onClick={onPrevWeek}
        style={{
          borderRadius: "8px 0 0 8px",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title="Previous Week"
      />

      <Popover
        content={calendarContent}
        title={null}
        trigger="click"
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
        placement="bottomLeft"
        overlayStyle={{ padding: 0 }}
        overlayInnerStyle={{ padding: 0, borderRadius: 8 }}
      >
        <Button
          style={{
            borderRadius: 0,
            borderLeft: 0,
            borderRight: 0,
            minWidth: 200,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 500,
            backgroundColor: calendarOpen
              ? token.colorPrimaryBg
              : token.colorBgContainer,
            borderColor: calendarOpen ? token.colorPrimary : token.colorBorder,
            color: calendarOpen ? token.colorPrimary : token.colorText,
          }}
          title="Click to open calendar"
        >
          <Space size={8}>
            <CalendarOutlined style={{ fontSize: 16 }} />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontSize: 13,
                  lineHeight: 1.2,
                  color: "inherit",
                  fontWeight: 500,
                }}
              >
                {weekStart} - {weekEnd}
              </Text>
              <Text
                style={{
                  fontSize: 11,
                  lineHeight: 1,
                  color: "inherit",
                  opacity: 0.7,
                }}
              >
                Click to select week
              </Text>
            </div>
          </Space>
        </Button>
      </Popover>

      <Button
        onClick={onCurrentWeek}
        style={{
          borderRadius: 0,
          borderLeft: 0,
          borderRight: 0,
          height: 40,
          minWidth: 100,
          fontWeight: 500,
          backgroundColor: dayjs(currentWeek).isSame(dayjs(), "week")
            ? token.colorPrimaryBg
            : token.colorBgContainer,
          borderColor: dayjs(currentWeek).isSame(dayjs(), "week")
            ? token.colorPrimary
            : token.colorBorder,
          color: dayjs(currentWeek).isSame(dayjs(), "week")
            ? token.colorPrimary
            : token.colorText,
        }}
        title="Go to current week"
      >
        Current Week
      </Button>

      <Button
        icon={<RightOutlined />}
        onClick={onNextWeek}
        style={{
          borderRadius: "0 8px 8px 0",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title="Next Week"
      />
    </Space.Compact>
  );
};

export default WeekCalendarSelector;
